/**
 * FridgeIQ — Transaction Log
 * Shows all sessions for a machine with expandable receipt + video player.
 */

import { useState, useEffect } from "react";
import { updateSessionNotes, resolveAbnormalSession } from "../services/dynamodb";
import { getSessionVideoUrls } from "../services/s3";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const fmtDate = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-US", {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    hour12: true,
  });
};

function StatusBadge({ status }) {
  const map = {
    paid:     { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)", color: "#10b981", label: "Paid" },
    abnormal: { bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.3)",  color: "#f87171", label: "⚠️ Abnormal" },
    empty:    { bg: "rgba(107,114,128,0.12)", border: "rgba(107,114,128,0.3)", color: "#6b7280", label: "Empty" },
    resolved: { bg: "rgba(96,165,250,0.12)", border: "rgba(96,165,250,0.3)", color: "#60a5fa", label: "✓ Resolved" },
  };
  const s = map[status] || map.empty;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`,
      color: s.color, borderRadius: 6,
      padding: "2px 10px", fontSize: 11, fontWeight: 600,
    }}>{s.label}</span>
  );
}

function VideoPlayer({ session }) {
  const [urls, setUrls]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setTab] = useState("cam0");

  useEffect(() => {
    getSessionVideoUrls(session).then(u => {
      setUrls(u);
      setLoading(false);
    });
  }, [session]);

  if (loading) return (
    <div style={{ padding: 24, textAlign: "center", color: "#4b5563", fontSize: 13 }}>
      Loading videos...
    </div>
  );

  const currentUrl = activeTab === "cam0" ? urls?.cam0Url : urls?.cam1Url;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["cam0", "cam1"].map(cam => (
          <button key={cam} onClick={() => setTab(cam)} style={{
            background: activeTab === cam ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${activeTab === cam ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.08)"}`,
            color: activeTab === cam ? "#10b981" : "#6b7280",
            borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
          }}>
            {cam === "cam0" ? "📷 CAM0 Wide" : "📷 CAM1 Shelf"}
          </button>
        ))}
      </div>
      {currentUrl ? (
        <video
          key={currentUrl}
          src={currentUrl}
          controls
          style={{ width: "100%", borderRadius: 8, background: "#000", maxHeight: 320 }}
        />
      ) : (
        <div style={{
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 8, padding: 32, textAlign: "center", color: "#4b5563", fontSize: 13,
        }}>
          No video available for this camera
        </div>
      )}
    </div>
  );
}

function SessionRow({ session, onUpdate }) {
  const [expanded, setExpanded]     = useState(false);
  const [notes, setNotes]           = useState(session.operator_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [resolving, setResolving]   = useState(false);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    await updateSessionNotes(session.machine_id, session.session_id, notes);
    setSavingNotes(false);
    onUpdate();
  };

  const handleResolve = async () => {
    setResolving(true);
    await resolveAbnormalSession(session.machine_id, session.session_id);
    setResolving(false);
    onUpdate();
  };

  const items = session.items_taken || [];
  const billable = items.filter(i => !i.abnormal);

  return (
    <div style={{
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${session.status === "abnormal" ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.06)"}`,
      borderRadius: 12, overflow: "hidden", marginBottom: 8,
    }}>
      {/* Row header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex", alignItems: "center", gap: 16,
          padding: "14px 18px", cursor: "pointer",
          background: expanded ? "rgba(255,255,255,0.02)" : "transparent",
        }}
      >
        <div style={{ fontSize: 11, color: "#6b7280", minWidth: 140 }}>
          {fmtDate(session.timestamp)}
        </div>
        <div style={{ flex: 1, fontSize: 13, color: "#d1d5db" }}>
          {items.length === 0
            ? <span style={{ color: "#4b5563" }}>No items taken</span>
            : billable.map(i => `${i.quantity}x ${i.name}`).join(", ")
          }
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#10b981", minWidth: 70, textAlign: "right" }}>
          {session.status === "empty" ? "—" : fmt(session.grand_total)}
        </div>
        <StatusBadge status={session.status} />
        <div style={{ fontSize: 13, color: "#4b5563" }}>{expanded ? "▲" : "▼"}</div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 16 }}>

            {/* Left — Receipt + notes */}
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Receipt
              </div>
              {billable.length > 0 ? (
                <div style={{ marginBottom: 16 }}>
                  {billable.map((item, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
                      fontSize: 13,
                    }}>
                      <span style={{ color: "#d1d5db" }}>{item.quantity}x {item.name}</span>
                      <span style={{ color: "#10b981" }}>{fmt(item.line_total)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontWeight: 700 }}>
                    <span style={{ color: "#9ca3af" }}>Total</span>
                    <span style={{ color: "#10b981" }}>{fmt(session.grand_total)}</span>
                  </div>
                </div>
              ) : (
                <div style={{ color: "#4b5563", fontSize: 13, marginBottom: 16 }}>No billable items</div>
              )}

              {/* Abnormal items */}
              {items.filter(i => i.abnormal).length > 0 && (
                <div style={{
                  background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: 8, padding: 12, marginBottom: 16,
                }}>
                  <div style={{ fontSize: 11, color: "#f87171", fontWeight: 600, marginBottom: 6 }}>
                    ⚠️ Abnormal Items
                  </div>
                  {items.filter(i => i.abnormal).map((item, i) => (
                    <div key={i} style={{ fontSize: 12, color: "#fca5a5", marginBottom: 2 }}>
                      {item.quantity}x "{item.name}" — {item.abnormal_reason}
                    </div>
                  ))}
                </div>
              )}

              {/* Session notes from Claude */}
              {session.session_notes && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    AI Notes
                  </div>
                  <div style={{
                    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 8, padding: 10, fontSize: 12, color: "#9ca3af", lineHeight: 1.5,
                  }}>
                    {session.session_notes}
                  </div>
                </div>
              )}

              {/* Operator notes */}
              <div>
                <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Operator Notes
                </div>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add notes about this session..."
                  rows={3}
                  style={{
                    width: "100%", background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
                    padding: 10, color: "#f9fafb", fontSize: 12, resize: "vertical",
                    outline: "none",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button onClick={handleSaveNotes} disabled={savingNotes} style={{
                    background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)",
                    color: "#10b981", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                  }}>
                    {savingNotes ? "Saving..." : "Save Notes"}
                  </button>
                  {session.status === "abnormal" && (
                    <button onClick={handleResolve} disabled={resolving} style={{
                      background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.3)",
                      color: "#60a5fa", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
                    }}>
                      {resolving ? "Resolving..." : "✓ Mark Resolved"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right — Video */}
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Session Video
              </div>
              <VideoPlayer session={session} />
              <div style={{ marginTop: 8, fontSize: 11, color: "#4b5563" }}>
                Duration: {session.duration_secs ? `${Number(session.duration_secs).toFixed(1)}s` : "—"} ·
                Session: {session.session_id}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TransactionLog({ sessions, loading, onUpdate }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = sessions.filter(s => {
    if (filter !== "all" && s.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const items = (s.items_taken || []).map(i => i.name.toLowerCase()).join(" ");
      return items.includes(q) || s.session_id.includes(q);
    }
    return true;
  });

  const counts = {
    all: sessions.length,
    paid: sessions.filter(s => s.status === "paid").length,
    abnormal: sessions.filter(s => s.status === "abnormal").length,
    empty: sessions.filter(s => s.status === "empty").length,
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#4b5563" }}>
      Loading transactions...
    </div>
  );

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {["all", "paid", "abnormal", "empty"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            background: filter === f ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${filter === f ? "rgba(16,185,129,0.35)" : "rgba(255,255,255,0.08)"}`,
            color: filter === f ? "#10b981" : "#6b7280",
            borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600,
          }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{ marginLeft: 6, opacity: 0.7 }}>({counts[f]})</span>
          </button>
        ))}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search items..."
          style={{
            marginLeft: "auto", background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8,
            padding: "6px 14px", color: "#f9fafb", fontSize: 12, outline: "none", width: 200,
          }}
        />
      </div>

      {/* Session rows */}
      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#4b5563", fontSize: 13 }}>
          No sessions found
        </div>
      ) : (
        filtered.map(s => (
          <SessionRow key={s.session_id} session={s} onUpdate={onUpdate} />
        ))
      )}
    </div>
  );
}
